import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  signal,
  type OnDestroy,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CurrentBrandScope,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  TrackByFunctions,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  LegacyDepositValueNormalization,
  TreatmentTemplate,
} from '@principle-theorem/principle-core';
import {
  DepositCaptureStrategy,
  IImplementsTreatmentTemplate,
  type IBrand,
  type IDepositTreatmentOverride,
  type IPractice,
  type IStaffer,
  type ITreatmentTemplate,
  type TreatmentTemplateTreatments,
} from '@principle-theorem/principle-core/interfaces';
import {
  DAYS_OF_WEEK,
  STEP_SIZE,
  filterUndefined,
  snapshot,
  type INamedDocument,
  type WithRef,
  isSameRef,
} from '@principle-theorem/shared';
import { DepositTreatmentTemplateFormGroup } from '../../../practices/patient-portal/deposit-settings-form/deposit-treatment-template.formgroup';
import { get, omit } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UpsertTreatmentTemplateStore } from './upsert-treatment-template.store';

export interface ITreatmentTemplateData {
  brand?: WithRef<IBrand>;
  depositOverride?: IDepositTreatmentOverride;
  treatmentTemplate?: WithRef<ITreatmentTemplate>;
}

export interface ITreatmentTemplateResult {
  depositOverride: IDepositTreatmentOverride;
  treatmentTemplate: ITreatmentTemplate;
}

interface ITreatmentTemplateFormData {
  name: string;
  description: string;
  duration: number;
  isPublic: boolean;
  treatment: TreatmentTemplateTreatments;
  enabledPractices: EnabledPracticesFormData;
  implementedBy: ImplementedByFormData;
}

interface IImplementorTreatmentPair {
  enabled: boolean;
  treatment?: TreatmentTemplateTreatments;
  duration?: number;
}

interface IPracticeFormData {
  enabled: boolean;
}

type EnabledPracticesFormData = Record<string, IPracticeFormData>;
type ImplementedByFormData = Record<string, IImplementorTreatmentPair>;

@Component({
  selector: 'pr-upsert-treatment-template',
  templateUrl: './upsert-treatment-template.component.html',
  styleUrls: ['./upsert-treatment-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UpsertTreatmentTemplateComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  trackByPractice = TrackByFunctions.ref<INamedDocument<IPractice>>();
  trackByStaffer = TrackByFunctions.ref<INamedDocument<IStaffer>>();
  readonly stepSize = signal(STEP_SIZE);
  staffer$: Observable<WithRef<IStaffer>>;
  form: TypedFormGroup<ITreatmentTemplateFormData> =
    new TypedFormGroup<ITreatmentTemplateFormData>({
      name: new TypedFormControl('', Validators.required),
      description: new TypedFormControl(''),
      duration: new TypedFormControl(undefined, [
        Validators.required,
        Validators.min(this.stepSize()),
      ]),
      treatment: new TypedFormControl<TreatmentTemplateTreatments | undefined>(
        undefined,
        Validators.required
      ),
      isPublic: new TypedFormControl(false),
      enabledPractices: new TypedFormGroup<EnabledPracticesFormData>({}),
      implementedBy: new TypedFormGroup<ImplementedByFormData>({}),
    });
  /**
   * Shared with the Deposit Settings template dialog so both surfaces enforce
   * one set of validity rules for the same persisted override.
   */
  readonly depositForm: DepositTreatmentTemplateFormGroup;
  update = false;

  constructor(
    private _dialogRef: MatDialogRef<
      UpsertTreatmentTemplateComponent,
      ITreatmentTemplateResult
    >,
    public store: UpsertTreatmentTemplateStore,
    private _brandScope: CurrentBrandScope,
    private _organisation: OrganisationService,

    @Inject(MAT_DIALOG_DATA) public data?: ITreatmentTemplateData
  ) {
    this.staffer$ = this._organisation.staffer$.pipe(filterUndefined());

    const depositOverride = this.data?.depositOverride;
    const inheritedCaptureStrategy =
      LegacyDepositValueNormalization.captureStrategy(
        this.data?.brand?.settings.onlineBookings?.deposit?.captureStrategy ??
          DepositCaptureStrategy.ChargeOnBookingRequest
      );
    this.depositForm = new DepositTreatmentTemplateFormGroup({
      depositRequired: depositOverride?.depositRequired ?? false,
      amount:
        depositOverride?.depositRequired === true
          ? depositOverride.amount
          : undefined,
      captureStrategy:
        depositOverride?.depositRequired === true
          ? LegacyDepositValueNormalization.captureStrategy(
              depositOverride.captureStrategy ?? inheritedCaptureStrategy
            )
          : inheritedCaptureStrategy,
      inheritsCaptureStrategy:
        depositOverride?.depositRequired === true &&
        depositOverride.captureStrategy === undefined,
      appliesTo:
        depositOverride?.depositRequired === true
          ? depositOverride.appliesTo ?? []
          : [],
      depositRequiredDays:
        depositOverride?.depositRequired === true
          ? depositOverride.depositRequiredDays ?? [...DAYS_OF_WEEK]
          : [...DAYS_OF_WEEK],
    });
    this.depositForm.controls.depositRequired.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(() => this.depositForm.updateAmountValidators());

    if (this.data?.treatmentTemplate) {
      this.update = true;
      this.form.patchValue({
        ...omit(this.data.treatmentTemplate, [
          'enabledPractices',
          'implementedBy',
        ]),
        duration: TreatmentTemplate.getDuration(this.data.treatmentTemplate),
        treatment: TreatmentTemplate.getTreatment(this.data.treatmentTemplate),
      });
    }

    this.store.loadBrand(this._brandScope.doc$.pipe(filterUndefined()));

    this.store.practices$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((practices) => {
        practices.map((practice) => {
          let practiceIsEnabled = false;

          if (this.data?.treatmentTemplate) {
            const enabledPractice =
              this.data.treatmentTemplate.enabledPractices.find((ep) =>
                isSameRef(ep.practice, practice)
              );
            if (enabledPractice) {
              practiceIsEnabled = true;
            }
          }

          this.enabledPractices.addControl(
            practice.ref.id,
            new TypedFormGroup<IPracticeFormData>({
              enabled: new TypedFormControl<boolean>(practiceIsEnabled),
            })
          );
        });
      });

    this.store.staff$.pipe(takeUntil(this._onDestroy$)).subscribe((staff) => {
      staff.map((staffer) => {
        if (!this.data?.treatmentTemplate) {
          this.implementedBy.addControl(
            staffer.ref.id,
            new TypedFormGroup<IImplementorTreatmentPair>({
              enabled: new TypedFormControl<boolean>(false),
              treatment: new TypedFormControl<TreatmentTemplateTreatments>(),
              duration: new TypedFormControl(
                undefined,
                Validators.min(this.stepSize())
              ),
            })
          );
          return;
        }

        const template = this.data.treatmentTemplate;
        const implementor = TreatmentTemplate.getImplementor(template, staffer);

        this.implementedBy.addControl(
          staffer.ref.id,
          new TypedFormGroup<IImplementorTreatmentPair>({
            enabled: new TypedFormControl<boolean>(!!implementor),
            treatment: new TypedFormControl<TreatmentTemplateTreatments>(
              this._implementorTreatment(template, implementor)
            ),
            duration: new TypedFormControl(
              this._implementorDuration(template, implementor),
              Validators.min(this.stepSize())
            ),
          })
        );
      });
    });

    this._organisation.practice$
      .pipe(filterUndefined(), takeUntil(this._onDestroy$))
      .subscribe((practice) => {
        const path = `settings.timeline.stepSizeInMins`;
        const stepSize = get(practice, path, STEP_SIZE) as number;
        this.stepSize.set(stepSize);
      });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  get enabledPractices(): TypedFormGroup<EnabledPracticesFormData> {
    return this.form.controls
      .enabledPractices as TypedFormGroup<EnabledPracticesFormData>;
  }

  get implementedBy(): TypedFormGroup<ImplementedByFormData> {
    return this.form.controls
      .implementedBy as TypedFormGroup<ImplementedByFormData>;
  }

  async toggleAllStaff(checked: boolean): Promise<void> {
    const staff = await snapshot(this.store.staff$.pipe(filterUndefined()));
    staff.map((staffer) => {
      this.getImplementationPair(staffer).controls.enabled.setValue(checked);
    });
  }

  getPracticeFormGroup(
    practice: INamedDocument<IPractice>
  ): TypedFormGroup<IPracticeFormData> {
    return this.enabledPractices.controls[
      practice.ref.id
    ] as TypedFormGroup<IPracticeFormData>;
  }

  getImplementationPair(
    staffer: INamedDocument<IStaffer>
  ): TypedFormGroup<IImplementorTreatmentPair> {
    return this.implementedBy.controls[
      staffer.ref.id
    ] as TypedFormGroup<IImplementorTreatmentPair>;
  }

  async save(): Promise<void> {
    if (!this.form.valid || !this.depositForm.valid) {
      return;
    }

    const data: ITreatmentTemplateFormData = this.form.getRawValue();

    const treatmentTemplate: ITreatmentTemplate = TreatmentTemplate.init({
      ...this.data?.treatmentTemplate,
      name: data.name,
      description: data.description,
      isPublic: data.isPublic,
      defaltDuration: data.duration,
      defaultTreatment: data.treatment,
    });

    TreatmentTemplate.resetPractices(treatmentTemplate);

    const practices = await snapshot(this.store.practices$);
    practices.map((practice) => {
      const practiceData = data.enabledPractices[practice.ref.id];
      if (practiceData?.enabled) {
        TreatmentTemplate.addPractice(treatmentTemplate, practice);
      }
    });

    TreatmentTemplate.resetImplementors(treatmentTemplate);
    const staff = await snapshot(this.store.staff$);
    staff.map((staffer) => {
      const implementor: IImplementorTreatmentPair | undefined =
        data.implementedBy[staffer.ref.id];
      if (implementor && implementor.enabled) {
        const treatment = implementor.treatment ?? data.treatment;
        const duration = implementor.duration ?? data.duration;
        TreatmentTemplate.updateImplementor(
          treatmentTemplate,
          staffer,
          treatment,
          duration
        );
      }
    });

    const depositOverride: IDepositTreatmentOverride =
      this.depositForm.toOverride();
    this._dialogRef.close({ treatmentTemplate, depositOverride });
  }

  private _implementorDuration(
    template: WithRef<ITreatmentTemplate>,
    implementor: IImplementsTreatmentTemplate | undefined
  ): number | undefined {
    return implementor?.duration === TreatmentTemplate.getDuration(template)
      ? undefined
      : implementor?.duration;
  }

  private _implementorTreatment(
    template: WithRef<ITreatmentTemplate>,
    implementor: IImplementsTreatmentTemplate | undefined
  ): TreatmentTemplateTreatments | undefined {
    return isSameRef(
      implementor?.treatment,
      TreatmentTemplate.getTreatment(template)
    )
      ? undefined
      : implementor?.treatment;
  }
}
